import { environment } from 'environments/environment';

export type ApiEnv = 'prod' | 'dev' | 'local';

export interface RuntimeConfig {
  name: string;
  appUrl: string;
  apiUrl: string;
  odataUrl?: string;
  signalRHubUrl?: string;
  apiLoggingEnabled?: boolean;
  apiLogging?: {
    enabled?: boolean;
    retentionDays?: number;
    maxTotalBytes?: number;
    maxLineBytes?: number;
    directory?: string;
    filePrefix?: string;
  };
  msal?: {
    redirectUri?: string;
    postLogoutRedirectUri?: string;
  };
}

const STORAGE_KEY = 'pb.customer.apiEnv';

export function getCurrentApiEnv(): ApiEnv {
  try {
    const raw = localStorage.getItem(STORAGE_KEY) as ApiEnv | null;
    if (raw === 'prod' || raw === 'dev' || raw === 'local') return raw;
  } catch {}

  try {
    const url = String((environment as any)?.apiConfig?.apiUrl || '').toLowerCase();
    if (url.includes('api-dev.postalbridge.com')) return 'dev';
    if (url.includes('api.postalbridge.com')) return 'prod';
  } catch {}

  return 'local';
}

function resolveConfigPath(env?: ApiEnv): string {
  switch (env ?? getCurrentApiEnv()) {
    case 'dev':
      return 'assets/config.dev.json';
    case 'local':
      return 'assets/config.local.json';
    case 'prod':
    default:
      return 'assets/config.prod.json';
  }
}

export function loadRuntimeConfig() {
  return () => async () => {
    const path = resolveConfigPath();

    try {
      const res = await fetch(path, { cache: 'no-store' });
      if (!res.ok) return;
      const cfg = (await res.json()) as RuntimeConfig;

      environment.appUrl = cfg.appUrl ?? environment.appUrl;

      // SignalR hub URL (optional)
      (environment as any).signalRHubUrl = (cfg.signalRHubUrl ?? (environment as any).signalRHubUrl);

      environment.apiConfig = {
        ...environment.apiConfig,
        apiUrl: cfg.apiUrl ?? environment.apiConfig.apiUrl,
        odataUrl: cfg.odataUrl ?? environment.apiConfig.odataUrl,
      };

      environment.msalConfig = {
        ...environment.msalConfig,
        auth: {
          ...environment.msalConfig.auth,
          redirectUri: cfg.msal?.redirectUri ?? environment.msalConfig.auth.redirectUri,
          postLogoutRedirectUri:
            cfg.msal?.postLogoutRedirectUri ??
            (environment.msalConfig.auth as any).postLogoutRedirectUri,
        },
      };

      // Optional API logging overrides
      try {
        const envAny = environment as any;
        const base = envAny.apiLogging ?? {};

        const enabled =
          cfg.apiLoggingEnabled ??
          cfg.apiLogging?.enabled;

        envAny.apiLogging = {
          ...base,
          ...(cfg.apiLogging ?? {}),
          ...(typeof enabled === 'boolean' ? { enabled } : {}),
        };
      } catch {
        // ignore
      }
    } catch {
      // ignore
    }
  };
}
